import { Mesh, Vector3 } from 'three';
import { ProjectRenderer } from '../ProjectRenderer';
import { ProductModule } from '../objects/ProductModule';
import { BarType, UserDataProp } from '../types';
import { UUID } from '@/types/types';
import { sample } from 'lodash';

export class ColoringManager {
	private _selectedColor: string | null = null;
	private productModuleGroups: Map<UUID, Set<UUID>> = new Map();
	private productModuleGroupByModule: Map<UUID, UUID> = new Map();

	constructor(private renderer: ProjectRenderer) {}

	private get modulesManager() {
		return this.renderer.modulesManager;
	}

	get selectedColor() {
		return this._selectedColor;
	}

	set selectedColor(color: string | null) {
		this._selectedColor = color;
	}

	handleColorChange(productModule: ProductModule, part: Mesh) {
		if (!this.selectedColor) return;

		const before = this.modulesManager.historyManager.capture();
		const isObjectBar = [BarType.HORIZONTAL, BarType.VERTICAL].includes(
			part.userData[UserDataProp.BAR_TYPE]
		);

		if (!isObjectBar) {
			productModule.setPartColor(part, this.selectedColor);
			this.modulesManager.historyManager.commit(before);
			return;
		}

		productModule.setBarsColor(part, this.selectedColor);

		const barType = part.userData[UserDataProp.BAR_TYPE] as BarType;
		this.propagateBarColorToGroup(productModule, barType, this.selectedColor);
		this.modulesManager.historyManager.commit(before);
	}

	applyBarsColorsAfterJoin(draggedId: UUID) {
		const draggedModule = this.modulesManager.productModules.get(draggedId);
		if (!draggedModule) return;

		const otherModules = Array.from(
			this.modulesManager.productModules.values()
		).filter((productModule) => productModule.id !== draggedId);
		if (!otherModules.length) return;

		const { groups: oldGroups, groupByModule: oldGroupByModule } =
			this.buildGroupsForModules(otherModules);
		const oldGridIndex = this.getGridIndexForModules(otherModules);
		const orderedNeighborGroups = this.getOrderedAdjacentGroupIds(
			draggedModule,
			oldGridIndex,
			oldGroupByModule
		);

		this.updateProductModuleGroups();

		const groupId = this.productModuleGroupByModule.get(draggedId);
		if (!groupId) return;

		const group = this.productModuleGroups.get(groupId);
		if (!group || group.size < 2) return;

		for (const barType of [BarType.HORIZONTAL, BarType.VERTICAL]) {
			const groupColor = this.getFirstGroupColor(
				orderedNeighborGroups,
				oldGroups,
				barType
			);
			const draggedColor = draggedModule.getBarColor(barType);
			const finalColor = groupColor || draggedColor;
			if (!finalColor) continue;

			for (const moduleId of group) {
				const productModule = this.modulesManager.productModules.get(moduleId);
				if (!productModule) continue;
				productModule.setBarTypeColor(barType, finalColor);
			}
		}
	}

	setRandomColors(colors: string[]) {
		if (!this.modulesManager.productModules.size) return;

		const before = this.modulesManager.historyManager.capture();

		this.updateProductModuleGroups();
		this.applyRandomBarColorsToGroups(colors);

		this.modulesManager.productModules.forEach((productModule) => {
			productModule.setPartsRandomColors(colors);
		});

		this.modulesManager.historyManager.commit(before);
	}

	private updateProductModuleGroups() {
		const modules = Array.from(this.modulesManager.productModules.values());
		const { groups, groupByModule } = this.buildGroupsForModules(modules);

		this.productModuleGroups = groups;
		this.productModuleGroupByModule = groupByModule;
	}

	private buildGroupsForModules(modules: ProductModule[]) {
		const adjacency = this.buildProductModuleAdjacency(modules);
		const visited = new Set<UUID>();
		const groups: Map<UUID, Set<UUID>> = new Map();
		const groupByModule: Map<UUID, UUID> = new Map();

		for (const productModule of modules) {
			const moduleId = productModule.id;
			if (visited.has(moduleId)) continue;

			const groupId = moduleId;
			const group = new Set<UUID>();
			const stack = [moduleId];

			while (stack.length) {
				const currentId = stack.pop() as UUID;
				if (visited.has(currentId)) continue;

				visited.add(currentId);
				group.add(currentId);
				groupByModule.set(currentId, groupId);

				const neighbors = adjacency.get(currentId);
				if (!neighbors) continue;

				for (const neighborId of neighbors) {
					if (visited.has(neighborId)) continue;
					stack.push(neighborId);
				}
			}

			groups.set(groupId, group);
		}

		return {
			groups,
			groupByModule,
		};
	}

	private getFirstGroupColor(
		orderedGroupIds: UUID[],
		groups: Map<UUID, Set<UUID>>,
		barType: BarType
	) {
		for (const groupId of orderedGroupIds) {
			const group = groups.get(groupId);
			if (!group) continue;

			const color = this.getGroupColor(group, barType);
			if (color) return color;
		}

		return '';
	}

	private getGroupColor(group: Set<UUID>, barType: BarType) {
		const orderedIds = Array.from(group).sort();

		for (const moduleId of orderedIds) {
			const module = this.modulesManager.productModules.get(moduleId);
			if (!module) continue;

			const color = module.getBarColor(barType);
			if (color) return color;
		}

		return '';
	}

	private buildProductModuleAdjacency(modules: ProductModule[]) {
		const adjacency: Map<UUID, Set<UUID>> = new Map();
		const gridIndex = this.getGridIndexForModules(modules);

		for (const productModule of modules) {
			adjacency.set(productModule.id, new Set());
		}

		for (const productModule of modules) {
			const neighbors = this.getAdjacentModuleIdsForModule(
				productModule,
				gridIndex
			);
			if (!neighbors.size) continue;

			const adjacencySet = adjacency.get(productModule.id);
			if (!adjacencySet) continue;

			for (const neighborId of neighbors) {
				adjacencySet.add(neighborId);
				const neighborSet = adjacency.get(neighborId);
				if (!neighborSet) continue;
				neighborSet.add(productModule.id);
			}
		}

		return adjacency;
	}

	private getGridIndexForModules(modules: ProductModule[]) {
		const gridIndex: Map<string, Set<UUID>> = new Map();

		for (const productModule of modules) {
			const coords =
				this.modulesManager.modulesGrid.getModuleGridCoords(productModule);

			for (const coord of coords) {
				const key = `${coord.x},${coord.y},${coord.z}`;
				const existing = gridIndex.get(key);
				if (existing) {
					existing.add(productModule.id);
					continue;
				}

				gridIndex.set(key, new Set([productModule.id]));
			}
		}

		return gridIndex;
	}

	private getNeighborOffsets() {
		const offsets: Vector3[] = [];
		const yLevels = [0, -1, 1];

		for (const y of yLevels) {
			for (let x = -1; x <= 1; x++) {
				for (let z = -1; z <= 1; z++) {
					if (x === 0 && y === 0 && z === 0) continue;
					offsets.push(new Vector3(x, y, z));
				}
			}
		}

		return offsets;
	}

	private getOrderedAdjacentGroupIds(
		draggedModule: ProductModule,
		gridIndex: Map<string, Set<UUID>>,
		groupByModule: Map<UUID, UUID>
	) {
		const neighborOffsets = this.getNeighborOffsets();

		const orderedGroupIds: UUID[] = [];
		const visitedGroups = new Set<UUID>();
		const coords =
			this.modulesManager.modulesGrid.getModuleGridCoords(draggedModule);

		coords.sort((a, b) => a.x - b.x || a.y - b.y || a.z - b.z);

		for (const coord of coords) {
			for (const offset of neighborOffsets) {
				const key = `${coord.x + offset.x},${coord.y + offset.y},${
					coord.z + offset.z
				}`;
				const occupying = gridIndex.get(key);
				if (!occupying) continue;

				const orderedIds = Array.from(occupying).sort();
				for (const moduleId of orderedIds) {
					const groupId = groupByModule.get(moduleId);
					if (!groupId) continue;
					if (visitedGroups.has(groupId)) continue;

					visitedGroups.add(groupId);
					orderedGroupIds.push(groupId);
				}
			}
		}

		return orderedGroupIds;
	}

	private getAdjacentModuleIdsForModule(
		productModule: ProductModule,
		gridIndex: Map<string, Set<UUID>>
	) {
		const neighborOffsets = this.getNeighborOffsets();

		const neighbors = new Set<UUID>();
		const coords =
			this.modulesManager.modulesGrid.getModuleGridCoords(productModule);

		for (const coord of coords) {
			for (const offset of neighborOffsets) {
				const key = `${coord.x + offset.x},${coord.y + offset.y},${
					coord.z + offset.z
				}`;
				const occupying = gridIndex.get(key);
				if (!occupying) continue;

				for (const occupyingId of occupying) {
					if (occupyingId === productModule.id) continue;
					neighbors.add(occupyingId);
				}
			}
		}

		return neighbors;
	}

	private applyRandomBarColorsToGroups(colors: string[]) {
		for (const group of this.productModuleGroups.values()) {
			const horizontalColor = sample(colors);
			const verticalColor = sample(colors);
			if (!horizontalColor && !verticalColor) continue;

			for (const moduleId of group) {
				const productModule = this.modulesManager.productModules.get(moduleId);
				if (!productModule) continue;

				productModule.setBarTypeColor(BarType.HORIZONTAL, horizontalColor!);
				productModule.setBarTypeColor(BarType.VERTICAL, verticalColor!);
			}
		}
	}

	private propagateBarColorToGroup(
		productModule: ProductModule,
		barType: BarType,
		color: string
	) {
		this.updateProductModuleGroups();

		const groupId = this.productModuleGroupByModule.get(productModule.id);
		if (!groupId) return;

		const group = this.productModuleGroups.get(groupId);
		if (!group || group.size < 2) return;

		for (const moduleId of group) {
			const otherModule = this.modulesManager.productModules.get(moduleId);
			if (!otherModule) continue;
			otherModule.setBarTypeColor(barType, color);
		}
	}
}
